'use client';

import { MotionMaybe, useMotionControls } from '@/utils/animation';
import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getChildHeadingVariant } from '@/utils/headingLevel';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { parseImagePosition } from '@/utils/parseImagePosition';
import { Fragment } from 'react';
import { Button } from './atoms/Button';
import { MarkDown } from './atoms/MarkDown';

const normalizeGroup = (value) => {
    const v = String(value ?? '')
        .toLowerCase()
        .trim();

    if (['table', 'rows', 'features', 'comparison', 'plans'].includes(v)) {
        return 'table';
    }
    if (['cta', 'ctas', 'buttons', 'button'].includes(v)) return 'ctas';
    if (v === 'all') return 'all';
    return 'content';
};

const normalizeString = (value) =>
    value == null || typeof value === 'boolean' ? '' : String(value).trim();
const normalizeDisplayString = (value) =>
    normalizeString(value)
        .replace(/&#x20;/gi, ' ')
        .replace(/&#32;/gi, ' ')
        .replace(/&nbsp;/gi, ' ')
        .trim();
const normalizeHexColor = (value) => {
    const raw = normalizeString(value);
    if (!raw) return '';
    const withHash = raw.startsWith('#') ? raw : `#${raw}`;
    if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(withHash)) {
        return withHash.toUpperCase();
    }
    return '';
};
const getReadableTextColor = (hexColor) => {
    const normalized = normalizeHexColor(hexColor);
    if (!normalized) return '#111827';
    const hex = normalized.slice(1);
    const expanded =
        hex.length === 3
            ? hex
                  .split('')
                  .map((c) => `${c}${c}`)
                  .join('')
            : hex;
    const r = Number.parseInt(expanded.slice(0, 2), 16);
    const g = Number.parseInt(expanded.slice(2, 4), 16);
    const b = Number.parseInt(expanded.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.62 ? '#111827' : '#FFFFFF';
};

const normalizeStatus = (rawStatus, rawIcon = '', rawText = '') => {
    const merged = normalizeString(
        rawStatus || rawIcon || rawText
    ).toLowerCase();

    if (
        [
            'check',
            'yes',
            'true',
            'included',
            'available',
            'supported',
            '1',
        ].includes(merged) ||
        merged.includes('fa-check')
    ) {
        return 'check';
    }

    if (
        [
            'x',
            'no',
            'false',
            'not included',
            'excluded',
            'unavailable',
            'not supported',
            '0',
        ].includes(merged) ||
        merged.includes('fa-xmark')
    ) {
        return 'x';
    }

    return '';
};

const normalizeCell = (cell) => {
    if (cell != null && typeof cell === 'object' && !Array.isArray(cell)) {
        const status = normalizeStatus(cell.Status, cell.Icon, cell.Value);
        return {
            status,
            text: normalizeDisplayString(cell.Text ?? ''),
        };
    }

    const raw = normalizeDisplayString(cell);
    const status = normalizeStatus(raw, raw, raw);
    return {
        status,
        text: status ? '' : raw,
    };
};

function CellValue({ status, text, isDark }) {
    const mutedText = isDark ? 'text-white/60' : 'text-neutral-500';
    const hasCheck = status === 'check';
    const hasX = status === 'x';

    if (!hasCheck && !hasX && !text) {
        return <span className={`${mutedText} text-base`}>-</span>;
    }

    if (hasCheck || hasX) {
        return (
            <div className="inline-flex items-center justify-center gap-2">
                <i
                    className={`fa-solid ${
                        hasCheck ? 'fa-check' : 'fa-xmark'
                    } text-sm md:text-base ${
                        hasCheck
                            ? isDark
                                ? 'text-green-300'
                                : 'text-green-600'
                            : isDark
                            ? 'text-rose-300'
                            : 'text-rose-600'
                    }`}
                    aria-hidden="true"
                />
                <span className="sr-only">Included</span>
                {text && (
                    <MarkDown
                        className={`text-xs font-normal leading-5 md:text-sm md:leading-6 ${
                            isDark ? 'text-white' : 'text-neutral-800'
                        }`}
                    >
                        {text}
                    </MarkDown>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-1">
            <MarkDown
                className={`text-xs font-normal leading-5 md:text-sm md:leading-6 ${
                    isDark ? 'text-white' : 'text-neutral-800'
                }`}
            >
                {text}
            </MarkDown>
        </div>
    );
}

export default function ComparisonTable(props) {
    const {
        Anchor,
        Tagline,
        Header,
        Body,
        FeatureBlockBackgroundImage = '',
        FeatureBlockBackgroundImagePosition = '50',
        FeatureColumnHeader = 'Features',
        Plans = [],
        Sections = [],
        Footnote,
        ColorScheme = 'dark',
        VerticalSpacing = 'medium',
        Animation = 'fade',
        Duration = 0.6,
        AnimationGroup = 'table',
        EditorPreview = false,
    } = props;

    const safePlans = (Array.isArray(Plans) ? Plans : []).slice(0, 5);
    const fallbackPlans = [
        {
            Name: 'Plan A',
            Price: '',
            Description: '',
            Badge: '',
            BadgeColor: '',
            CTA: '',
            CTALink: '',
        },
    ];
    const plans = safePlans.length ? safePlans : fallbackPlans;

    const sections = Array.isArray(Sections) ? Sections : [];
    const featureColumnHeaderLabel =
        normalizeDisplayString(FeatureColumnHeader);
    const planHeadingVariant = getChildHeadingVariant(
        normalizeDisplayString(Header)
    );
    const normalizedFeatureBlockBackgroundImage = normalizeString(
        FeatureBlockBackgroundImage
    );
    const hasFeatureBlockBackgroundImage = Boolean(
        normalizedFeatureBlockBackgroundImage
    );
    const { x: featureBlockBackgroundX, y: featureBlockBackgroundY } =
        parseImagePosition(FeatureBlockBackgroundImagePosition, {
            x: 50,
            y: 50,
        });

    const {
        backgroundColor,
        textColor,
        headerColor,
        taglineColor,
        secondaryButton,
        thirdButton,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);

    const vSpace = getVerticalSpacing(VerticalSpacing);
    const isDark =
        String(textColor).includes('white') ||
        String(headerColor).includes('white') ||
        String(taglineColor).includes('white');

    const sectionHeaderBgClass = isDark ? 'bg-white/10' : 'bg-neutral-900/10';
    const sectionHeaderClass = `${sectionHeaderBgClass} ${
        isDark ? 'text-white' : 'text-neutral-800'
    }`;
    const tableSurfaceClass = 'bg-transparent';
    const dividerClass = 'border-transparent';
    const planCellClass = `${dividerClass} ${
        isDark ? 'text-white' : 'text-neutral-800'
    }`;
    const rowDividerClass = '';
    const cellLeftBorderClass = '';
    const stickyColumnTextClass = isDark ? 'text-white' : 'text-neutral-800';
    const featureRowBgClass = isDark ? 'bg-white/[0.05]' : 'bg-neutral-900/5';
    const featureRowClass = featureRowBgClass;
    const glassCellClass = 'backdrop-blur-sm';
    const stickyHeaderSurfaceClass = sectionHeaderBgClass;
    const stickyFeatureSurfaceClass = featureRowBgClass;
    const stickyHeaderOverlayClass = '';
    const stickyCellOverlayClass = '';
    const stickySectionOverlayClass = stickyCellOverlayClass;
    const stickyFeatureOverlayClass = stickyCellOverlayClass;

    const normalizedGroup = normalizeGroup(AnimationGroup);
    const controlsGroup =
        normalizedGroup === 'all' ? 'content' : normalizedGroup;
    const motion = useMotionControls({
        preset: Animation,
        duration: Number(Duration) || 0.6,
        editorPreview: EditorPreview,
        group: controlsGroup,
        keyBase: 'comparison-table',
    });

    const animateContent =
        motion.enableAnim &&
        (normalizedGroup === 'all' || normalizedGroup === 'content');
    const animateTable =
        motion.enableAnim &&
        (normalizedGroup === 'all' || normalizedGroup === 'table');
    const animateCTAs =
        motion.enableAnim &&
        (normalizedGroup === 'all' || normalizedGroup === 'ctas');

    const ctaMotionEnabled =
        normalizedGroup === 'all'
            ? animateCTAs
            : animateCTAs && motion.enableChild('ctas');

    const { hasAny: hasMainCTAs, element: mainCTAs } = getCTA(props, {
        secondaryVariant: secondaryButton,
        outlineVariant: thirdButton,
        buttonAlign: 'justify-center',
        wrapSpacing: 'mt-10 lg:mt-12',
        enableAnim: ctaMotionEnabled,
        motionProps: motion.childMotionProps,
    });

    return (
        <section
            id={Anchor}
            className={`editable-component relative overflow-hidden px-6 lg:px-8 ${backgroundColor} ${vSpace}`}
            style={backgroundStyle}
            data-json="comparisontable"
        >
            <div className="relative z-40 mx-auto max-w-7xl">
                {(Tagline || Header || Body) && (
                    <MotionMaybe
                        enable={
                            normalizedGroup === 'all'
                                ? animateContent
                                : animateContent &&
                                  motion.enableChild('content')
                        }
                        nodeKey={`${motion.remountKey}-content`}
                        motionProps={motion.childMotionProps}
                        className="mx-auto max-w-3xl text-center"
                    >
                        {Tagline && (
                            <MarkDown
                                className={`font-display text-base uppercase leading-7 tracking-wide ${taglineColor}`}
                            >
                                {normalizeDisplayString(Tagline)}
                            </MarkDown>
                        )}

                        {Header && (
                            <MarkDown
                                variant="h2"
                                className={`mt-2 font-display text-2xl tracking-tight md:text-3xl lg:text-4xl ${headerColor}`}
                            >
                                {normalizeDisplayString(Header)}
                            </MarkDown>
                        )}

                        {Body && (
                            <MarkDown
                                className={`mt-6 text-base font-text leading-8 md:text-lg ${textColor}`}
                            >
                                {normalizeDisplayString(Body)}
                            </MarkDown>
                        )}
                    </MotionMaybe>
                )}

                <MotionMaybe
                    enable={
                        normalizedGroup === 'all'
                            ? animateTable
                            : animateTable && motion.enableChild('table')
                    }
                    nodeKey={`${motion.remountKey}-table`}
                    motionProps={motion.childMotionProps}
                    className={`mt-10 overflow-hidden rounded-[var(--shape-radius)] lg:mt-12 ${tableSurfaceClass}`}
                >
                    <div className="overflow-x-auto">
                        <table className="min-w-full border-separate text-left [border-spacing:1px_1px]">
                            <caption className="sr-only">
                                Comparison table
                            </caption>
                            <thead>
                                <tr className={sectionHeaderClass}>
                                    <th
                                        scope="col"
                                        className={`sticky left-0 z-20 min-w-[10rem] p-3 text-left font-display text-base font-semibold leading-6 tracking-tight md:min-w-[16rem] md:px-5 md:py-4 md:text-2xl md:leading-8 ${glassCellClass} ${planCellClass} ${stickyHeaderSurfaceClass} ${stickyColumnTextClass} relative isolate ${stickyHeaderOverlayClass}`}
                                    >
                                        {hasFeatureBlockBackgroundImage && (
                                            <span
                                                aria-hidden="true"
                                                className="absolute inset-0 z-0 bg-cover bg-no-repeat opacity-35"
                                                style={{
                                                    backgroundImage: `url(${normalizedFeatureBlockBackgroundImage})`,
                                                    backgroundPosition: `${featureBlockBackgroundX}% ${featureBlockBackgroundY}%`,
                                                }}
                                            />
                                        )}
                                        {featureColumnHeaderLabel && (
                                            <span className="relative z-10">
                                                {featureColumnHeaderLabel}
                                            </span>
                                        )}
                                    </th>
                                    {plans.map((plan, idx) => {
                                        const planBadgeColor =
                                            normalizeHexColor(plan?.BadgeColor);
                                        const hasCustomBadgeColor =
                                            Boolean(planBadgeColor);
                                        const planBadgeTextColor =
                                            getReadableTextColor(
                                                planBadgeColor
                                            );

                                        return (
                                            <th
                                                key={`${
                                                    plan?.Name || 'plan'
                                                }-${idx}`}
                                                scope="col"
                                                className={`min-w-[9rem] p-3 align-top md:min-w-[14rem] md:p-5 ${glassCellClass} ${rowDividerClass} ${cellLeftBorderClass} ${planCellClass} ${sectionHeaderClass}`}
                                            >
                                                <div className="space-y-2">
                                                    {normalizeDisplayString(
                                                        plan?.Badge
                                                    ) && (
                                                        <span
                                                            className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide md:px-2.5 md:py-1 md:text-[11px] ${
                                                                hasCustomBadgeColor
                                                                    ? ''
                                                                    : isDark
                                                                    ? 'bg-white/15 text-white'
                                                                    : 'bg-primary-100 text-primary-700'
                                                            }`}
                                                            style={
                                                                hasCustomBadgeColor
                                                                    ? {
                                                                          backgroundColor:
                                                                              planBadgeColor,
                                                                          color: planBadgeTextColor,
                                                                      }
                                                                    : undefined
                                                            }
                                                        >
                                                            {normalizeDisplayString(
                                                                plan?.Badge
                                                            )}
                                                        </span>
                                                    )}
                                                    <MarkDown
                                                        variant={
                                                            planHeadingVariant
                                                        }
                                                        className={`font-display text-sm leading-5 md:text-xl md:leading-6 ${
                                                            isDark
                                                                ? 'text-white'
                                                                : 'text-neutral-800'
                                                        }`}
                                                    >
                                                        {normalizeDisplayString(
                                                            plan?.Name
                                                        ) || `Plan ${idx + 1}`}
                                                    </MarkDown>

                                                    {normalizeDisplayString(
                                                        plan?.Price
                                                    ) && (
                                                        <MarkDown
                                                            className={`text-sm font-semibold md:text-lg ${
                                                                isDark
                                                                    ? 'text-white'
                                                                    : 'text-neutral-800'
                                                            }`}
                                                        >
                                                            {normalizeDisplayString(
                                                                plan?.Price
                                                            )}
                                                        </MarkDown>
                                                    )}

                                                    {normalizeDisplayString(
                                                        plan?.Description
                                                    ) && (
                                                        <MarkDown
                                                            className={`text-[10px] font-normal leading-4 md:text-xs md:leading-5 ${textColor}`}
                                                        >
                                                            {normalizeDisplayString(
                                                                plan?.Description
                                                            )}
                                                        </MarkDown>
                                                    )}

                                                    {normalizeDisplayString(
                                                        plan?.CTA
                                                    ) && (
                                                        <Button
                                                            asChild
                                                            variant="primary"
                                                            size="sm"
                                                            className="mt-1 w-full px-2.5 py-1 text-[11px] md:w-auto"
                                                        >
                                                            <a
                                                                href={
                                                                    normalizeString(
                                                                        plan?.CTALink
                                                                    ) || '#'
                                                                }
                                                            >
                                                                {normalizeDisplayString(
                                                                    plan?.CTA
                                                                )}
                                                            </a>
                                                        </Button>
                                                    )}
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>

                            <tbody>
                                {sections.map((section, sectionIndex) => {
                                    const rows = Array.isArray(section?.Rows)
                                        ? section.Rows.slice(0, 40)
                                        : [];
                                    const sectionTitle =
                                        normalizeDisplayString(
                                            section?.Title
                                        ) || `Section ${sectionIndex + 1}`;

                                    return (
                                        <Fragment
                                            key={`section-${
                                                section?.Title || 'group'
                                            }-${sectionIndex}`}
                                        >
                                            <tr className={sectionHeaderClass}>
                                                <th
                                                    scope="row"
                                                    className={`sticky left-0 z-10 min-w-[10rem] p-3 font-display text-[10px] font-semibold uppercase tracking-wide md:min-w-[16rem] md:px-5 md:py-4 md:text-xs ${glassCellClass} ${rowDividerClass} ${dividerClass} ${stickyHeaderSurfaceClass} ${stickyColumnTextClass} relative isolate ${stickySectionOverlayClass}`}
                                                >
                                                    <span className="relative z-10">
                                                        {sectionTitle}
                                                    </span>
                                                </th>
                                                {plans.map(
                                                    (_, sectionPlanIdx) => (
                                                        <td
                                                            key={`section-cell-${sectionIndex}-${sectionPlanIdx}`}
                                                            aria-hidden="true"
                                                            className={`p-3 md:px-5 md:py-4 ${glassCellClass} ${rowDividerClass} ${cellLeftBorderClass} ${dividerClass} ${sectionHeaderClass}`}
                                                        />
                                                    )
                                                )}
                                            </tr>

                                            {rows.map((row, rowIndex) => {
                                                const rowBgClass =
                                                    featureRowClass;

                                                return (
                                                    <tr
                                                        key={`row-${
                                                            row?.Feature ||
                                                            'feature'
                                                        }-${rowIndex}`}
                                                        className={rowBgClass}
                                                    >
                                                        <th
                                                            scope="row"
                                                            className={`sticky left-0 z-10 min-w-[10rem] px-3 py-2 align-top text-xs font-semibold leading-5 md:min-w-[16rem] md:px-5 md:py-4 md:text-sm md:leading-6 ${glassCellClass} ${rowDividerClass} ${dividerClass} ${stickyFeatureSurfaceClass} ${stickyColumnTextClass} relative isolate ${stickyFeatureOverlayClass}`}
                                                        >
                                                            <MarkDown className="relative z-10 font-display text-xs font-semibold leading-5 md:text-sm md:leading-6">
                                                                {normalizeDisplayString(
                                                                    row?.Feature
                                                                ) ||
                                                                    `Feature ${
                                                                        rowIndex +
                                                                        1
                                                                    }`}
                                                            </MarkDown>
                                                        </th>

                                                        {plans.map(
                                                            (_, planIndex) => {
                                                                const values =
                                                                    Array.isArray(
                                                                        row?.Values
                                                                    )
                                                                        ? row.Values
                                                                        : [];
                                                                const cell =
                                                                    values[
                                                                        planIndex
                                                                    ];
                                                                const {
                                                                    status,
                                                                    text,
                                                                } =
                                                                    normalizeCell(
                                                                        cell
                                                                    );

                                                                return (
                                                                    <td
                                                                        key={`cell-${planIndex}`}
                                                                        className={`px-3 py-2 text-center align-top md:px-5 md:py-4 ${glassCellClass} ${cellLeftBorderClass} ${rowDividerClass} ${dividerClass} ${featureRowClass} ${
                                                                            isDark
                                                                                ? 'text-white'
                                                                                : 'text-neutral-800'
                                                                        }`}
                                                                    >
                                                                        <CellValue
                                                                            status={
                                                                                status
                                                                            }
                                                                            text={
                                                                                text
                                                                            }
                                                                            isDark={
                                                                                isDark
                                                                            }
                                                                        />
                                                                    </td>
                                                                );
                                                            }
                                                        )}
                                                    </tr>
                                                );
                                            })}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </MotionMaybe>

                {Footnote && (
                    <MarkDown
                        className={`mt-4 text-center text-xs leading-6 ${
                            isDark ? 'text-white/70' : 'text-neutral-500'
                        }`}
                    >
                        {normalizeDisplayString(Footnote)}
                    </MarkDown>
                )}

                {hasMainCTAs && mainCTAs}
            </div>
        </section>
    );
}
